import React, { createContext, useContext, useEffect, useState } from 'react'

export interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  name?: string
  has_subscription?: boolean
  subscription_tier?: string
  subscription_expires_at?: string
  email_verified?: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string, recaptchaToken?: string) => Promise<void>
  register: (email: string, password: string, firstName: string, lastName: string, recaptchaToken?: string) => Promise<void>
  googleLogin: (idToken: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  requestPasswordReset: (email: string) => Promise<void>
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>
  sendVerificationEmail: () => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://bmwfinder-api-25f5f05731c4.herokuapp.com' 
  : 'http://localhost:8083'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const publicUser: User = { id: 'public', email: '', name: 'Guest', has_subscription: true, subscription_tier: 'free' }
  const [user, setUser] = useState<User | null>(publicUser)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing session on app load
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        setUser(publicUser)
        setLoading(false)
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const userData = await response.json()
        setUser({ ...userData, has_subscription: true })
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('auth_token')
        setUser(publicUser)
      }
    } catch (error) {
      //console.error('Auth check failed:', error)
      localStorage.removeItem('auth_token')
      setUser(publicUser)
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string, recaptchaToken?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, recaptcha_token: recaptchaToken }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Login failed')
      }

      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      setUser({ ...data.user, has_subscription: true })
    } catch (error) {
      throw error
    }
  }

  const register = async (email: string, password: string, firstName: string, lastName: string, recaptchaToken?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName, recaptcha_token: recaptchaToken }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Registration failed')
      }

      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      setUser({ ...data.user, has_subscription: true })
    } catch (error) {
      throw error
    }
  }

  const googleLogin = async (idToken: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id_token: idToken }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Google login failed')
      }

      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      setUser({ ...data.user, has_subscription: true })
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    setUser(publicUser)
  }

  const requestPasswordReset = async (email: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Password reset request failed')
      }
    } catch (error) {
      throw error
    }
  }

  const confirmPasswordReset = async (token: string, newPassword: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/confirm-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: newPassword }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Password reset failed')
      }
    } catch (error) {
      throw error
    }
  }

  const sendVerificationEmail = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/send-verification-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to send verification email')
      }
    } catch (error) {
      throw error
    }
  }

  const verifyEmail = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Email verification failed')
      }

      // Refresh user data after verification
      await checkAuthStatus()
    } catch (error) {
      throw error
    }
  }

  const value = {
    user,
    loading,
    login,
    register,
    googleLogin,
    logout,
    isAuthenticated: !!user,
    requestPasswordReset,
    confirmPasswordReset,
    sendVerificationEmail,
    verifyEmail,
    refreshUser: checkAuthStatus,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
